import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import { toast } from "react-toastify";

import NoResults from "../../../../../../Common/NoResults";
import { TEMPLATE_SIDES } from "../../../../../../Common/Template/TemplateContext";
import StyledScrollbar from "../../../../../../Common/UIComponents/StyledScrollbar";
import { useAccessStatus } from "../../../../../../grace-period/hooks/useAccessStatus";
import CoaPreviewModal from "../../../../../../Product/Report/Components/Preview/CoaPreviewModal";
import {
  buildEnvHistoryCoaFilePaths,
  fetchEnvCoaPreviewFromTemplate,
} from "../../../Preview/envCoaPreviewHelper";
// import { Switch } from "antd";
import FileCard from "./FileCard";

import { getEnvReportHistory } from "../../../../../../../actions/envAnalytics";

import { ReactComponent as LoadingDots } from "../../../../../../../assets/images/environment/3-dots-scale.svg";
import pumpkin from "../../../../../../../assets/images/environment/pumpkin.gif";
import { ReactComponent as CloseButton } from "../../../../../../../assets/images/environment/WhiteCloseBlueBackground.svg";

import "./ReportHistory.css";

export default function ReportHistory({ report, specsData, setShowReportHistory }) {
  const { areResultsLocked } = useAccessStatus();
  const [filePaths, setFilePaths] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showPartialOnly, setShowPartialOnly] = useState(false); // set default to 'all_report'
  const [previewFile, setPreviewFile] = useState(null);

  const controller = useRef();

  const previewFilePaths = useMemo(
    () => (previewFile ? buildEnvHistoryCoaFilePaths(previewFile) : null),
    [previewFile],
  );

  const loadEnvironmentTemplatePreview = (template, signal) => (
    fetchEnvCoaPreviewFromTemplate({
      templateId: template.template_id,
      report,
      specsData,
      activeDate: previewFile?.received_on || previewFile?.received_date,
      signal,
    })
  );

  /**
   * Get file paths by page
   */
  async function apiGetAllFilePaths() {
    const abortController = new AbortController();
    if (controller.current) {
      controller.current.abort();
    }
    controller.current = abortController;

    setLoadingHistory(true);
    const {
      success, message, paths, canceled,
    } = await getEnvReportHistory(report, showPartialOnly, controller.current.signal);

    if (success) {
      setFilePaths(paths);
    } else {
      setFilePaths([]);
      if (!canceled) {
        toast.error(message);
      }
    }
    setLoadingHistory(false);
  }

  /**
   * componentDidMount - make api call to get all report paths
   */
  useEffect(() => {
    apiGetAllFilePaths();
  }, [showPartialOnly]); // eslint-disable-line

  /**
   * filter api for partial only files
   */
  // eslint-disable-next-line
  const handleShowPartialOnly = () => {
    setShowPartialOnly(!showPartialOnly);
  };

  const handlePreviewFile = (file) => {
    if (areResultsLocked) {
      return;
    }
    const filePathsForPreview = buildEnvHistoryCoaFilePaths(file);
    if (!filePathsForPreview) {
      toast.error("Unable to preview, no valid file path for this report!");
      return;
    }
    setPreviewFile(file);
  };

  const messageStyle = {
    position: "absolute",
    fontSize: "16px",
    color: "rgb(117, 138, 185)",
    fontFamily: "Roboto",
    marginTop: "144px",
  };

  return (
    <div className="env-report-history-container">
      {previewFilePaths && !areResultsLocked && (
        <CoaPreviewModal
          key={Object.values(previewFilePaths)[0]?.[0]}
          filePaths={previewFilePaths}
          report={report}
          handleCloseForPreview={() => setPreviewFile(null)}
          side={TEMPLATE_SIDES.ENVIRONMENTAL}
          loadTemplatePreview={loadEnvironmentTemplatePreview}
        />
      )}
      <div className="env-report-history-header">
        <div className="env-report-history-header-title">
          <span>Report History</span>
          {/* <span className="env-report-history-partial-switch">
            <Switch
              size="small"
              checked={showPartialOnly}
              onChange={handleShowPartialOnly}
            />
            <span>Partial</span>
          </span> */}
        </div>
        <CloseButton
          height={18}
          width={18}
          onClick={() => setShowReportHistory(false)}
        />
      </div>
      <div className="env-report-history-table">
        {loadingHistory && (
          <div className="env-report-history-table-loading">
            <LoadingDots fill="#B3BFDB" width={20} />
          </div>
        )}
        {!loadingHistory && filePaths.length > 0
        && (
        <StyledScrollbar>
          {filePaths.map((file, index) => (
            <FileCard
              key={index}
              file={file}
              onPreview={handlePreviewFile}
            />
          ))}
        </StyledScrollbar>
        )}
        {!loadingHistory && filePaths.length === 0 && <NoResults messageStyle={messageStyle} message="No reports found" image={pumpkin} />}
      </div>
    </div>
  );
}
